import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionList,
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  SkeletonRows,
  TextAreaField,
  useToast,
} from '@csa/ui';
import { api, ApiError } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';

/**
 * The approver's queue.
 *
 * Maker-checker, so the list the server returns already excludes the
 * viewer's own requests and anything they have already decided. This page
 * does not filter -- if a request is on this screen, deciding it is
 * permitted, and a UI that hid a button the server would have honoured is
 * not a control.
 *
 * Every decision carries a comment, enforced by the shared schema at both
 * ends. "Approved" with no reason is the finding an auditor writes up.
 */

interface PendingApproval {
  readonly id: string;
  readonly jobId: string;
  readonly kind: 'sql' | 'api';
  readonly requestedByUpn: string;
  readonly targetKey: string;
  readonly environment: string;
  readonly payloadSummary: string;
  readonly payloadHash: string;
  readonly justification: string;
  readonly matchedRuleName: string;
  readonly requiredApprovals: number;
  readonly grantedCount: number;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly viewerMayDecide: boolean;
  readonly viewerBlockedReason: string | null;
}

/**
 * A pending script publication, as it arrives over the wire.
 *
 * Declared here rather than imported from `@csa/shared`, whose
 * `ScriptPublication` types the timestamps as `Date`. That is true
 * on the server and false after JSON, and a `Date` that is really a string
 * type-checks right up until something calls `.getTime()` on it. The same
 * reason `PendingApproval` above is declared locally.
 */
interface ScriptPublication {
  readonly id: string;
  readonly scriptId: string;
  readonly scriptName: string;
  readonly version: number;
  readonly statement: string;
  readonly requestedVisibility: 'role' | 'organisation';
  readonly requestedRoleName: string | null;
  readonly requestedByUpn: string;
  readonly justification: string;
  readonly requiredApprovals: number;
  readonly grantedCount: number;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly canDecide: boolean;
}

/** The shortest comment the shared schema accepts. Mirrored so the button can explain itself. */
const MIN_COMMENT = 5;

export function ApprovalsPage(): ReactElement {
  const [deciding, setDeciding] = useState<{
    approval: PendingApproval;
    decision: 'granted' | 'rejected';
  } | null>(null);

  const approvals = useQuery({
    queryKey: ['approvals'],
    queryFn: ({ signal }) =>
      api.get<{ approvals: readonly PendingApproval[] }>('/api/approvals', { signal }),
    /**
     * Someone is waiting on the other side of this queue, and requests
     * expire on their own. A stale list means either a colleague blocked
     * needlessly or an approver working on a request that has already
     * lapsed.
     */
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  if (approvals.isError) {
    return (
      <ErrorState
        title="Could not load the approval queue"
        message={
          approvals.error instanceof ApiError ? approvals.error.message : 'Unexpected error.'
        }
        action={<Button onClick={() => void approvals.refetch()}>Retry</Button>}
      />
    );
  }

  const pending = approvals.data?.approvals ?? [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Approvals"
        description="Requests waiting on you. Your own requests never appear here."
      />

      {approvals.isPending ? (
        <SkeletonRows rows={4} />
      ) : pending.length === 0 ? (
        <EmptyState
          title="No executions waiting"
          description="Requests that match an approval rule and were raised by someone else appear here. Script publications, if any, are listed below."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {pending.map((approval) => (
            <ApprovalCard
              key={approval.id}
              approval={approval}
              onDecide={(decision) => setDeciding({ approval, decision })}
            />
          ))}
        </div>
      )}

      <ScriptPublicationQueue />

      {deciding ? (
        <DecisionModal
          approval={deciding.approval}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Script publications, on the same page as execution approvals.
 *
 * One inbox rather than two: an approver should have one place to look, and
 * a second page is a second thing to forget. But a distinct section with
 * its own card and its own wording, because the two decisions are not the
 * same and copy that blurs them is worse than a separate page.
 *
 * Rendered only when there is something in it. Someone who cannot publish
 * scripts gets an empty list from the server and so sees nothing at all,
 * which is the right outcome without a permission check here.
 */
function ScriptPublicationQueue(): ReactElement | null {
  const [deciding, setDeciding] = useState<{
    publication: ScriptPublication;
    decision: 'granted' | 'rejected';
  } | null>(null);

  const publications = useQuery({
    queryKey: ['script-publications'],
    queryFn: ({ signal }) =>
      api.get<{ publications: readonly ScriptPublication[] }>('/api/script-publications', {
        signal,
      }),
    refetchInterval: 60_000,
  });

  const pending = publications.data?.publications ?? [];
  if (pending.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold text-body">Script publications</h2>
        <p className="text-xs text-subtle">
          Requests to put a script in front of other people. Approving publishes advice, not
          access — anyone who runs it still needs their own permission on the target.
        </p>
      </div>

      {pending.map((publication) => (
        <ScriptPublicationCard
          key={publication.id}
          publication={publication}
          onDecide={(decision) => setDeciding({ publication, decision })}
        />
      ))}

      {deciding ? (
        <PublicationDecisionModal
          publication={deciding.publication}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
        />
      ) : null}
    </section>
  );
}

function ScriptPublicationCard({
  publication,
  onDecide,
}: {
  publication: ScriptPublication;
  onDecide: (decision: 'granted' | 'rejected') => void;
}): ReactElement {
  const tz = useTimeZone();

  return (
    <Card className="flex flex-col gap-3 p-3">
      <header className="flex flex-wrap items-center gap-2">
        <Badge tone="brand">script</Badge>
        <span className="text-sm font-medium text-body">{publication.scriptName}</span>
        <Badge tone="neutral">v{publication.version}</Badge>
        <span className="text-sm text-body">{publication.requestedByUpn}</span>

        <span className="ml-auto flex items-center gap-1.5 text-xs text-subtle">
          <ClockIcon className="size-4" aria-hidden />
          <span title={tz.titleFor(publication.expiresAt)}>
            expires {tz.format(publication.expiresAt)}
          </span>
        </span>
      </header>

      {/*
        The statement in full, which is the entire point of this card. An
        approver who cannot read the script is being asked to rubber-stamp
        a name, and the risk this control answers -- a plausible title over
        a query that reads something it should not -- is invisible from
        anything but the text.
      */}
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-subtle">
          The statement being published
        </p>
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded border border-line bg-sunken p-2 font-mono text-xs">
          {publication.statement}
        </pre>
      </div>

      <DescriptionList
        items={[
          {
            label: 'Audience',
            value:
              publication.requestedVisibility === 'organisation'
                ? 'Everyone with the script library'
                : (publication.requestedRoleName ?? 'A role'),
          },
          { label: 'Why', value: publication.justification },
          {
            label: 'Approvals',
            value: `${publication.grantedCount} of ${publication.requiredApprovals} granted`,
          },
          {
            label: `Raised (${tz.timeZone})`,
            value: (
              <span title={tz.titleFor(publication.createdAt)}>
                {tz.format(publication.createdAt)}
              </span>
            ),
          },
        ]}
      />

      {publication.canDecide ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<CheckIcon className="size-4" aria-hidden />}
            onClick={() => onDecide('granted')}
          >
            Publish
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<XMarkIcon className="size-4" aria-hidden />}
            onClick={() => onDecide('rejected')}
          >
            Reject
          </Button>
        </div>
      ) : (
        <Alert tone="info" title="You cannot decide this one">
          You have already decided on it.
        </Alert>
      )}
    </Card>
  );
}

function PublicationDecisionModal({
  publication,
  decision,
  onClose,
}: {
  publication: ScriptPublication;
  decision: 'granted' | 'rejected';
  onClose: () => void;
}): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ status: string; published: boolean }>(
        `/api/script-publications/${publication.id}/decision`,
        { decision, comment: comment.trim() },
      ),
    onSuccess: (result) => {
      toast({
        tone: decision === 'granted' ? 'success' : 'info',
        title: decision === 'granted' ? 'Published' : 'Rejected',
        message: result.published
          ? 'The script is now visible to its audience.'
          : 'Nothing changed about who can see the script.',
      });
      void queryClient.invalidateQueries({ queryKey: ['script-publications'] });
      onClose();
    },
  });

  // A rejection needs a real reason; the server enforces ten characters.
  const tooShort = decision === 'rejected' && comment.trim().length < 10;

  return (
    <Modal
      open
      onClose={onClose}
      dismissable={false}
      title={decision === 'granted' ? 'Publish this script' : 'Reject this publication'}
      description={`${publication.scriptName} v${publication.version} · ${publication.requestedByUpn}`}
      footer={
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            variant={decision === 'granted' ? 'primary' : 'danger'}
            loading={submit.isPending}
            disabled={tooShort}
            onClick={() => submit.mutate()}
          >
            {decision === 'granted' ? 'Publish' : 'Reject'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {decision === 'granted' ? (
          /*
            Deliberately different wording from the execution dialog, which
            says a job will start. Nothing starts here, and saying so is
            what stops an approver reading this as the heavier decision --
            or, worse, reading the execution one as this lighter one.
          */
          <Alert tone="warning" title="What publishing means">
            <p>
              This makes version {publication.version} visible to{' '}
              {publication.requestedVisibility === 'organisation'
                ? 'everyone who can use the script library'
                : (publication.requestedRoleName ?? 'the chosen role')}
              . Nothing runs, now or later: whoever uses it still needs their own permission on
              the target, their own justification, and their own approval if it writes.
            </p>
            <p className="mt-1">
              What you are vouching for is that the statement does what its name says. It binds
              to this exact version — a later edit needs a new decision.
            </p>
          </Alert>
        ) : (
          <Alert tone="info" title="What rejecting means">
            The script stays private to its author. They can amend it and ask again, which binds
            a new request to the new version.
          </Alert>
        )}

        <TextAreaField
          label="Your reason"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          rows={3}
          required={decision === 'rejected'}
          description={
            decision === 'rejected'
              ? 'At least 10 characters. Without one the author is sent back to guess.'
              : 'Optional, and recorded against your name.'
          }
          {...(submit.error
            ? {
                error:
                  submit.error instanceof ApiError
                    ? submit.error.message
                    : 'The decision could not be recorded.',
              }
            : {})}
        />
      </div>
    </Modal>
  );
}

function ApprovalCard({
  approval,
  onDecide,
}: {
  approval: PendingApproval;
  onDecide: (decision: 'granted' | 'rejected') => void;
}): ReactElement {
  const tz = useTimeZone();
  const remaining = approval.requiredApprovals - approval.grantedCount;

  return (
    <Card className="flex flex-col gap-3 p-3">
      <header className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{approval.kind}</Badge>
        <code className="font-mono text-xs">{approval.targetKey}</code>
        <Badge tone={approval.environment === 'prod' ? 'danger' : 'neutral'}>
          {approval.environment}
        </Badge>
        <span className="text-sm text-body">{approval.requestedByUpn}</span>

        <span className="ml-auto flex items-center gap-1.5 text-xs text-subtle">
          <ClockIcon className="size-4" aria-hidden />
          <span title={tz.titleFor(approval.expiresAt)}>
            expires {tz.format(approval.expiresAt)}
          </span>
        </span>
      </header>

      <DescriptionList
        items={[
          {
            label: 'What was requested',
            value: <span className="break-words">{approval.payloadSummary}</span>,
          },
          { label: 'Why', value: approval.justification },
          { label: 'Rule', value: approval.matchedRuleName },
          {
            label: 'Approvals',
            value: `${approval.grantedCount} of ${approval.requiredApprovals} granted${
              remaining > 0 ? ` · ${remaining} still needed` : ''
            }`,
          },
          {
            /*
              The hash the decision binds to. Editing and resubmitting
              produces a different hash and therefore a different request,
              so an approval can never be carried over to altered work --
              and showing it is what makes that rule legible rather than
              merely true.
            */
            label: 'Payload hash',
            value: <code className="font-mono text-xs">{approval.payloadHash.slice(0, 32)}…</code>,
          },
          {
            label: `Raised (${tz.timeZone})`,
            value: (
              <span title={tz.titleFor(approval.createdAt)}>{tz.format(approval.createdAt)}</span>
            ),
          },
        ]}
      />

      {approval.viewerMayDecide ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<CheckIcon className="size-4" aria-hidden />}
            onClick={() => onDecide('granted')}
          >
            Approve
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<XMarkIcon className="size-4" aria-hidden />}
            onClick={() => onDecide('rejected')}
          >
            Reject
          </Button>
          <span className="text-xs text-subtle">
            Both decisions require a comment, and both are recorded against your name.
          </span>
        </div>
      ) : (
        <Alert tone="info" title="You cannot decide this one">
          {approval.viewerBlockedReason ?? 'Another approver is required.'}
        </Alert>
      )}
    </Card>
  );
}

function DecisionModal({
  approval,
  decision,
  onClose,
}: {
  approval: PendingApproval;
  decision: 'granted' | 'rejected';
  onClose: () => void;
}): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      api.post<{
        status: string;
        grantedCount: number;
        requiredApprovals: number;
        released: boolean;
      }>(`/api/approvals/${approval.id}/decision`, { decision, comment: comment.trim() }),
    onSuccess: (result) => {
      toast({
        tone: decision === 'granted' ? 'success' : 'info',
        title: decision === 'granted' ? 'Approved' : 'Rejected',
        message: result.released
          ? 'The threshold was reached and the job has been released to run.'
          : decision === 'granted'
            ? `${result.grantedCount} of ${result.requiredApprovals} approvals recorded.`
            : 'Nothing was executed.',
      });
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      onClose();
    },
  });

  const tooShort = comment.trim().length < MIN_COMMENT;

  return (
    <Modal
      open
      onClose={onClose}
      // Not dismissable: a half-typed decision lost to a stray backdrop click
      // is a decision somebody retypes in a hurry.
      dismissable={false}
      title={decision === 'granted' ? 'Approve this request' : 'Reject this request'}
      description={`${approval.requestedByUpn} · ${approval.targetKey}`}
      footer={
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            variant={decision === 'granted' ? 'primary' : 'danger'}
            loading={submit.isPending}
            disabled={tooShort}
            onClick={() => submit.mutate()}
          >
            {decision === 'granted' ? 'Approve' : 'Reject'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {decision === 'granted' ? (
          <Alert tone="warning" title="What approving means">
            <p>
              {approval.grantedCount + 1 >= approval.requiredApprovals
                ? 'This is the last approval needed. The job will start as soon as you confirm.'
                : `This records one of ${approval.requiredApprovals} approvals. The job will not start until the rest are given.`}
            </p>
            <p className="mt-1">
              It runs against <code className="font-mono text-xs">{approval.targetKey}</code> in{' '}
              {approval.environment}, using a service account, not yours.
            </p>
          </Alert>
        ) : (
          <Alert tone="info" title="What rejecting means">
            The request is closed and nothing is executed. The requester can amend and resubmit,
            which raises a new request with a new payload hash.
          </Alert>
        )}

        <Card tone="default" className="p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-subtle">
            What you are deciding on
          </p>
          <p className="mt-1 break-words text-sm text-body">{approval.payloadSummary}</p>
          <p className="mt-2 text-xs text-muted">Stated reason: {approval.justification}</p>
        </Card>

        <TextAreaField
          label="Your reason"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          rows={4}
          required
          description={`Recorded in the audit trail against your name, and kept for seven years. At least ${MIN_COMMENT} characters.`}
          {...(submit.error
            ? {
                error:
                  submit.error instanceof ApiError
                    ? submit.error.message
                    : 'The decision could not be recorded.',
              }
            : {})}
        />
      </div>
    </Modal>
  );
}
